import React from "react";
import PropTypes from "prop-types";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { PAGINATION_TEXT, PAGINATION_CONFIG } from "./constants.js";
import { getPageRange, getItemRange } from "./utils.js";
import "./styles.scss";

export default function Pagination({
  totalPages,
  currentPage,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  totalItems,
  itemLabel = PAGINATION_TEXT.MESSAGES,
}) {
  const { MAX_VISIBLE_PAGES, ROWS_PER_PAGE_OPTIONS, ELLIPSIS } = PAGINATION_CONFIG;

  const { startPage, endPage, pageNumbers } = getPageRange(currentPage, totalPages, MAX_VISIBLE_PAGES);

  const { startItem, endItem } = getItemRange(currentPage, rowsPerPage, totalItems);

  const isFirst = currentPage === 0;
  const isLast = totalPages === 0 || currentPage >= totalPages - 1;

  return (
    <div className="pagination">
      {/* Info */}
      <div className="pagination__info">
        {totalItems ? (
          <>
            {PAGINATION_TEXT.SHOWING} <strong>{startItem}</strong> {PAGINATION_TEXT.TO} <strong>{endItem}</strong>{" "}
            {PAGINATION_TEXT.OF} <strong>{totalItems}</strong> {itemLabel}
          </>
        ) : (
          `No ${itemLabel} available`
        )}
      </div>

      <div className="pagination__controls">
        {/* Rows per page */}
        <div className="pagination__rows">
          <label htmlFor="rowsPerPage">{PAGINATION_TEXT.ROWS_PER_PAGE}</label>
          <select id="rowsPerPage" value={rowsPerPage} onChange={e => onRowsPerPageChange(Number(e.target.value))}>
            {ROWS_PER_PAGE_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="pagination__nav">
          {/* First */}
          <button
            className="pagination__btn"
            disabled={isFirst}
            onClick={() => onPageChange(0)}
            aria-label="First page"
          >
            <ChevronsLeft size={16} />
          </button>

          {/* Previous */}
          <button
            className="pagination__btn"
            disabled={isFirst}
            onClick={() => onPageChange(Math.max(0, currentPage - 1))}
            aria-label="Previous page"
          >
            <ChevronLeft size={16} />
          </button>

          {startPage > 0 && <span className="pagination__ellipsis">{ELLIPSIS}</span>}

          {/* Page numbers */}
          {pageNumbers.map(page => (
            <button
              key={page}
              className={`pagination__btn pagination__btn--page ${
                currentPage === page - 1 ? "pagination__btn--active" : ""
              }`}
              onClick={() => onPageChange(page - 1)}
            >
              {page}
            </button>
          ))}

          {endPage < totalPages && <span className="pagination__ellipsis">{ELLIPSIS}</span>}

          {/* Next */}
          <button
            className="pagination__btn"
            disabled={isLast}
            onClick={() => onPageChange(Math.min(totalPages - 1, currentPage + 1))}
            aria-label="Next page"
          >
            <ChevronRight size={16} />
          </button>

          {/* Last */}
          <button
            className="pagination__btn"
            disabled={isLast}
            onClick={() => onPageChange(totalPages - 1)}
            aria-label="Last page"
          >
            <ChevronsRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

Pagination.propTypes = {
  totalPages: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  itemLabel: PropTypes.string,
};
